/**
 * Maps the grouped dependents returned by the backend
 * (GET /v2/companies/:companyId/employees/:employeeId/coverage-dependents)
 * into the rows used by the stock employee detail.
 *
 * Each coverage item keeps the same shape the dependent modals already use
 * (edit dependent, edit dates, unsubscribe and remove association).
 *
 * @param {Array} groups - Backend response.
 * @param {Object} options
 * @param {number} options.employeeId
 * @param {Function} options.formatRut - (rut) => formatted rut
 * @param {Function} options.formatDate - (date) => formatted date
 * @param {Function} options.getRelationshipLabel - (relationship) => label
 * @param {Function} options.buildValidity - (startDate, endDate) => text
 * @returns {Array} Groups with `coverages` items
 */
export const toStockDependentGroups = (
  groups = [],
  { employeeId, formatRut, formatDate, getRelationshipLabel, buildValidity }
) =>
  groups.map((group, groupIndex) => {
    const rut = group.rut ? formatRut(group.rut) : '-';
    const fullName = group.fullName || '-';
    const birthdayDate = group.birthdayDate
      ? formatDate(group.birthdayDate)
      : '-';
    const relationshipLabel = getRelationshipLabel(group.relationship);
    const groupKey = `dependent-${group.dependentId || group.rut || groupIndex}`;

    const coverages = (group.coverages || []).map((coverage) => ({
      id: coverage.employeeCoverageDependentId,
      key: `${coverage.employeeCoverageId}-${coverage.employeeCoverageDependentId}`,
      employeeCoverageDependentId: coverage.employeeCoverageDependentId,
      dependentId: group.dependentId,
      employeeCoverageId: coverage.employeeCoverageId,
      employeeId: Number(employeeId),
      rut,
      fullName,
      birthdayDate,
      relationshipLabel: getRelationshipLabel(coverage.relationship),
      relationship: coverage.relationship,
      policyNumber: coverage.policyNumber || '-',
      groupName: coverage.groupName || '-',
      coverageName: coverage.coverageName || '-',
      startDate: coverage.startDate || null,
      endDate: coverage.endDate || null,
      requestStartDate: coverage.requestStartDate || null,
      requestEndDate: coverage.requestEndDate || null,
      vigencia: buildValidity(coverage.startDate, coverage.endDate),
      status: coverage.status,
      maintainerType: 'carga',
      canBeDeleted: coverage.status === 'ACTIVE'
    }));

    return {
      key: groupKey,
      dependentId: group.dependentId,
      rut,
      fullName,
      birthdayDate,
      relationship: group.relationship,
      relationshipLabel,
      status: group.status,
      policyNumbers: group.policyNumbers || [],
      coverageCount: group.coverageCount ?? coverages.length,
      activeCount: group.activeCount ?? 0,
      inactiveCount: group.inactiveCount ?? 0,
      coverages
    };
  });

// Flat list of coverage items, as the view used before grouping.
export const flattenStockDependentGroups = (groups = []) =>
  groups.flatMap((group) => group.coverages);
